/**
 * Mount transition for live Work UI — a short fade with a slight upward
 * settle, so tool steps, progress notes, and deliverables arrive instead of
 * popping. `enabled={false}` renders statically (history replays and
 * already-settled rows must not shimmer on open).
 */
import React, { useEffect, useRef } from 'react';
import { Animated, Easing } from 'react-native';
import PropTypes from 'prop-types';

function WorkAppear({ children, enabled = true, duration = 180, distance = 4, style }) {
  const progress = useRef(new Animated.Value(enabled ? 0 : 1)).current;

  useEffect(() => {
    if (!enabled) return undefined;
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    animation.start();
    return () => animation.stop();
    // Mount-only: re-running on prop churn would replay the entrance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Animated.View
      style={[
        style,
        {
          opacity: progress,
          transform: [{
            translateY: progress.interpolate({
              inputRange: [0, 1],
              outputRange: [distance, 0],
            }),
          }],
        },
      ]}
    >
      {children}
    </Animated.View>
  );
}

WorkAppear.propTypes = {
  children: PropTypes.node,
  enabled: PropTypes.bool,
  duration: PropTypes.number,
  distance: PropTypes.number,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default React.memo(WorkAppear);
